import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Megaphone, Plus, Eye, Clock, CheckCircle, XCircle, 
  DollarSign, Upload, ExternalLink, BarChart3, Info, AlertCircle,
  Video, Image, Link as LinkIcon, CreditCard, ArrowLeft, Globe,
  Building2, Mail, Phone, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import api from '../utils/api';
import { toFullUrl } from '../utils/imageUrl';
import { uploadToCloudinary } from '../utils/cloudinaryUpload';

// Fygaro payment links
const AD_PRICING = {
  "1_day": { 
    price: 25.00, 
    label: "1 Day", 
    days: 1,
    description: "Quick 24-hour campaign",
    fygaro_link: "https://www.fygaro.com/en/pb/2f3e62fd-40f0-4627-9fff-4a03eceabdc9/"
  },
  "1_week": { 
    price: 100.00, 
    label: "1 Week", 
    days: 7,
    description: "7-day visibility",
    fygaro_link: "https://www.fygaro.com/en/pb/68217283-4679-44f7-a121-5fec45ebf597/"
  },
  "1_month": { 
    price: 350.00, 
    label: "1 Month", 
    days: 30,
    description: "30-day exposure",
    fygaro_link: "https://www.fygaro.com/en/pb/3d93dc6d-c632-4f4f-ba1c-8dabce97654e/"
  },
  "3_months": { 
    price: 900.00, 
    label: "3 Months", 
    days: 90,
    description: "Best value!",
    fygaro_link: "https://www.fygaro.com/en/pb/4debe0e3-0f14-47c4-a136-da1b2e3abd4e/"
  }
};

const Advertising = () => {
  const navigate = useNavigate();
  const [myAds, setMyAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPricing, setSelectedPricing] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const logoInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  
  const [adForm, setAdForm] = useState({
    // Company Info
    company_name: '',
    company_logo_url: '',
    contact_email: '',
    contact_phone: '',
    
    // Ad Content
    title: '',
    tagline: '',
    description: '',
    
    // Media
    image_url: '',
    video_url: '',
    
    // Links
    website_url: '',
    cta_text: 'Learn More',
    cta_url: '',
    
    // Duration
    duration: '1_week'
  });

  useEffect(() => {
    fetchMyAds();
  }, []);

  const fetchMyAds = async () => {
    try {
      const response = await api.get('/advertisements/my');
      setMyAds(response.data);
    } catch (error) {
      console.error('Failed to fetch ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return null;
    
    const maxSize = type === 'video' ? 50 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`File must be less than ${type === 'video' ? '50MB' : '5MB'}`);
      return null;
    }
    
    const setUploading = type === 'logo' ? setUploadingLogo : 
                         type === 'image' ? setUploadingImage : setUploadingVideo;
    setUploading(true);
    
    try {
      const token = localStorage.getItem('token');
      const result = await uploadToCloudinary(file, 'ads', token);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} uploaded to cloud storage`);
      return result.url;
    } catch (error) {
      toast.error(`Failed to upload ${type}: ${error.message}`);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const url = await handleFileUpload(e.target.files[0], 'logo');
    if (url) setAdForm(prev => ({ ...prev, company_logo_url: url }));
  };

  const handleImageUpload = async (e) => {
    const url = await handleFileUpload(e.target.files[0], 'image');
    if (url) setAdForm(prev => ({ ...prev, image_url: url }));
  };

  const handleVideoUpload = async (e) => {
    const url = await handleFileUpload(e.target.files[0], 'video');
    if (url) setAdForm(prev => ({ ...prev, video_url: url }));
  };

  const handlePricingClick = (key) => {
    setSelectedPricing(key);
    setAdForm(prev => ({ ...prev, duration: key }));
    setShowPaymentModal(true);
  };

  const proceedToForm = () => {
    setShowPaymentModal(false);
    setShowForm(true);
  };

  const handlePayAndSubmit = () => {
    // Open Fygaro payment in new tab
    if (selectedPricing && AD_PRICING[selectedPricing]?.fygaro_link) {
      window.open(AD_PRICING[selectedPricing].fygaro_link, '_blank');
    }
    setShowPaymentModal(false);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!adForm.title) {
      toast.error('Please enter an ad title');
      return;
    }
    
    if (!adForm.image_url && !adForm.video_url) {
      toast.error('Please upload at least one image or video');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...adForm,
        price: AD_PRICING[adForm.duration]?.price || 100,
        duration_days: AD_PRICING[adForm.duration]?.days || 7,
        fygaro_link: AD_PRICING[adForm.duration]?.fygaro_link
      };

      await api.post('/advertisements/submit', payload);
      toast.success(
        'Ad submitted successfully! Complete payment via Fygaro to activate your ad.',
        { duration: 6000 }
      );
      setShowForm(false);
      resetForm();
      fetchMyAds();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit ad');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setAdForm({
      company_name: '',
      company_logo_url: '',
      contact_email: '',
      contact_phone: '',
      title: '',
      tagline: '',
      description: '',
      image_url: '',
      video_url: '',
      website_url: '',
      cta_text: 'Learn More',
      cta_url: '',
      duration: '1_week'
    });
  };

  const handlePayNow = (ad) => {
    const pricing = AD_PRICING[ad.duration];
    if (pricing?.fygaro_link) {
      window.open(pricing.fygaro_link, '_blank');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      active: 'bg-green-100 text-green-700',
      approved: 'bg-blue-100 text-blue-700',
      rejected: 'bg-red-100 text-red-700',
      paused: 'bg-slate-100 text-slate-700',
      expired: 'bg-gray-100 text-gray-600',
      awaiting_payment: 'bg-orange-100 text-orange-700'
    };
    return <Badge className={styles[status] || 'bg-slate-100'}>{status?.replace('_', ' ')}</Badge>;
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    const now = new Date();
    const days = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return days;
  };

  const selectedPrice = AD_PRICING[adForm.duration]?.price || 100;

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-slate-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-slate-50 p-3 sm:p-4 md:p-6 lg:p-8">
        <div className="max-w-5xl mx-auto">
          {/* Back to Dashboard */}
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-3 sm:mb-4 text-sm"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>

          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl font-outfit font-bold text-ocean-900">Advertising</h1>
              <p className="text-sm sm:text-base text-slate-600">Promote your company across the CTC Network</p>
            </div>
            <Button
              onClick={() => {
                setSelectedPricing('1_week');
                setAdForm(prev => ({ ...prev, duration: '1_week' }));
                setShowForm(true);
              }}
              className="bg-cyan-500 hover:bg-cyan-600 w-full sm:w-auto text-sm sm:text-base"
              data-testid="create-ad-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create New Ad
            </Button>
          </div>

          {/* Pricing Cards */}
          <Card className="mb-6 sm:mb-8">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <DollarSign className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-500" />
                Advertising Packages
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm">Select a package and create your ad</CardDescription>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 pt-0 sm:pt-0">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                {Object.entries(AD_PRICING).map(([key, { price, label, description }]) => (
                  <div 
                    key={key} 
                    onClick={() => handlePricingClick(key)}
                    className={`border rounded-lg sm:rounded-xl p-2 sm:p-4 text-center transition-all cursor-pointer hover:border-cyan-400 hover:shadow-lg ${
                      key === '3_months' ? 'bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-300 ring-2 ring-cyan-200' : ''
                    }`}
                    data-testid={`pricing-${key}`}
                  >
                    <p className="font-semibold text-sm sm:text-lg text-cyan-700">{label}</p>
                    <p className="text-xl sm:text-3xl font-bold text-ocean-900 my-1 sm:my-2">${price}</p>
                    <p className="text-[10px] sm:text-xs text-slate-500 hidden sm:block">{description}</p>
                    {key === '3_months' && (
                      <Badge className="mt-1 sm:mt-2 bg-cyan-500 text-[10px] sm:text-xs">Best Value</Badge>
                    )}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-2 sm:mt-3 w-full text-[10px] sm:text-xs h-7 sm:h-9"
                    >
                      Select
                    </Button>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold flex items-center gap-2 mb-2 sm:mb-3 text-sm sm:text-base">
                  <Info className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-cyan-500" />
                  What's Included
                </h4>
                <div className="grid sm:grid-cols-2 gap-1.5 sm:gap-2 text-xs sm:text-sm text-slate-600">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 flex-shrink-0" />
                    Pop-up ads across platform
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 flex-shrink-0" />
                    Upload logo and images
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 flex-shrink-0" />
                    Add videos to your ad
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 flex-shrink-0" />
                    Link to your website
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 flex-shrink-0" />
                    Real-time analytics
                  </div>
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-500 flex-shrink-0" />
                    Email reminder before expiry
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Modal */}
          <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
            <DialogContent className="w-[95vw] max-w-md p-4 sm:p-6">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
                  <CreditCard className="h-5 w-5 text-cyan-500" />
                  Create Your Ad
                </DialogTitle>
              </DialogHeader>
              {selectedPricing && (
                <div className="space-y-4 pt-4">
                  <div className="bg-cyan-50 p-4 rounded-lg text-center">
                    <p className="text-sm text-slate-600">Selected Package</p>
                    <p className="text-xl font-bold text-ocean-900">{AD_PRICING[selectedPricing].label}</p>
                    <p className="text-3xl font-bold text-cyan-600 mt-2">${AD_PRICING[selectedPricing].price}</p>
                  </div>
                  
                  <div className="space-y-2 text-sm text-slate-600">
                    <p className="font-medium">How it works:</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Fill out your ad details</li>
                      <li>Pay via Fygaro (secure payment)</li>
                      <li>Admin reviews and approves</li>
                      <li>Your ad goes live!</li>
                    </ol>
                  </div>

                  <Button
                    onClick={handlePayAndSubmit}
                    className="w-full bg-green-500 hover:bg-green-600"
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    Pay Now & Create Ad
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={proceedToForm}
                    className="w-full"
                  >
                    Create Ad First, Pay Later
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Ad Creation Form */}
          <Dialog open={showForm} onOpenChange={setShowForm}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Megaphone className="h-5 w-5 text-cyan-500" />
                  Create Your Advertisement
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6 pt-4">
                
                {/* Company Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2 border-b pb-2">
                    <Building2 className="h-4 w-4 text-cyan-500" />
                    Company Information
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 md:col-span-1">
                      <Label>Company Name</Label>
                      <Input
                        value={adForm.company_name}
                        onChange={(e) => setAdForm(prev => ({ ...prev, company_name: e.target.value }))}
                        placeholder="Your Company Name"
                        data-testid="ad-company-name"
                      />
                    </div>
                    <div className="col-span-2 md:col-span-1">
                      <Label>Company Logo</Label>
                      <div className="flex items-center gap-3">
                        {adForm.company_logo_url && (
                          <img src={adForm.company_logo_url} alt="Logo" className="w-12 h-12 object-contain rounded border" />
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => logoInputRef.current?.click()}
                          disabled={uploadingLogo}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                        </Button>
                        <input
                          ref={logoInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleLogoUpload}
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="flex items-center gap-1">
                        <Mail className="h-3 w-3" /> Contact Email
                      </Label>
                      <Input
                        type="email"
                        value={adForm.contact_email}
                        onChange={(e) => setAdForm(prev => ({ ...prev, contact_email: e.target.value }))}
                        placeholder="contact@yourcompany.com"
                      />
                    </div>
                    <div>
                      <Label className="flex items-center gap-1">
                        <Phone className="h-3 w-3" /> Contact Phone
                      </Label>
                      <Input
                        value={adForm.contact_phone}
                        onChange={(e) => setAdForm(prev => ({ ...prev, contact_phone: e.target.value }))}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>
                </div>

                {/* Ad Content */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2 border-b pb-2">
                    <FileText className="h-4 w-4 text-cyan-500" />
                    Ad Content
                  </h3>
                  
                  <div>
                    <Label>Ad Title *</Label>
                    <Input
                      value={adForm.title}
                      onChange={(e) => setAdForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Fast Caribbean Shipping - 50% Off!"
                      required
                      data-testid="ad-title"
                    />
                  </div>
                  
                  <div>
                    <Label>Tagline / Slogan</Label>
                    <Input
                      value={adForm.tagline}
                      onChange={(e) => setAdForm(prev => ({ ...prev, tagline: e.target.value }))}
                      placeholder="e.g., Your trusted freight partner since 2005"
                    />
                  </div>
                  
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={adForm.description}
                      onChange={(e) => setAdForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Tell people about your offer, services, or promotion..."
                      rows={3}
                    />
                  </div>
                </div>

                {/* Media Upload */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2 border-b pb-2">
                    <Image className="h-4 w-4 text-cyan-500" />
                    Media (At least one required)
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Ad Image</Label>
                      <div className="space-y-2">
                        {adForm.image_url && (
                          <img src={toFullUrl(adForm.image_url)} alt="Ad" className="w-full h-32 object-cover rounded border" />
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => imageInputRef.current?.click()}
                          disabled={uploadingImage}
                          className="w-full"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          {uploadingImage ? 'Uploading...' : 'Upload Image'}
                        </Button>
                        <input
                          ref={imageInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleImageUpload}
                        />
                        <p className="text-xs text-slate-500">Recommended: 800x400px, max 5MB</p>
                      </div>
                    </div>
                    
                    <div>
                      <Label>Ad Video (Optional)</Label>
                      <div className="space-y-2">
                        {adForm.video_url && (
                          <div className="flex items-center gap-2 p-3 bg-green-50 rounded border border-green-200">
                            <Video className="h-5 w-5 text-green-600" />
                            <span className="text-sm text-green-700">Video uploaded</span>
                          </div>
                        )}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => videoInputRef.current?.click()}
                          disabled={uploadingVideo}
                          className="w-full"
                        >
                          <Video className="h-4 w-4 mr-2" />
                          {uploadingVideo ? 'Uploading...' : 'Upload Video'}
                        </Button>
                        <input
                          ref={videoInputRef}
                          type="file"
                          accept="video/*"
                          className="hidden"
                          onChange={handleVideoUpload}
                        />
                        <p className="text-xs text-slate-500">Max 50MB (MP4, MOV)</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Links */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2 border-b pb-2">
                    <LinkIcon className="h-4 w-4 text-cyan-500" />
                    Links & Call-to-Action
                  </h3>
                  
                  <div>
                    <Label className="flex items-center gap-1">
                      <Globe className="h-3 w-3" /> Website URL
                    </Label>
                    <Input
                      value={adForm.website_url}
                      onChange={(e) => setAdForm(prev => ({ ...prev, website_url: e.target.value }))}
                      placeholder="https://www.yourcompany.com"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Call-to-Action Text</Label>
                      <Select
                        value={adForm.cta_text}
                        onValueChange={(v) => setAdForm(prev => ({ ...prev, cta_text: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Learn More">Learn More</SelectItem>
                          <SelectItem value="Get Quote">Get Quote</SelectItem>
                          <SelectItem value="Contact Us">Contact Us</SelectItem>
                          <SelectItem value="Visit Website">Visit Website</SelectItem>
                          <SelectItem value="Shop Now">Shop Now</SelectItem>
                          <SelectItem value="Book Now">Book Now</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>CTA Link URL</Label>
                      <Input
                        value={adForm.cta_url}
                        onChange={(e) => setAdForm(prev => ({ ...prev, cta_url: e.target.value }))}
                        placeholder="https://yourcompany.com/special-offer"
                      />
                      <p className="text-xs text-slate-500 mt-1">Where users go when they click</p>
                    </div>
                  </div>
                </div>

                {/* Duration & Pricing */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800 flex items-center gap-2 border-b pb-2">
                    <Clock className="h-4 w-4 text-cyan-500" />
                    Duration & Payment
                  </h3>
                  
                  <div>
                    <Label>Ad Duration</Label>
                    <Select
                      value={adForm.duration}
                      onValueChange={(v) => setAdForm(prev => ({ ...prev, duration: v }))}
                    >
                      <SelectTrigger data-testid="ad-duration">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(AD_PRICING).map(([key, { price, label }]) => (
                          <SelectItem key={key} value={key}>
                            {label} - ${price}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-4 rounded-lg border border-cyan-200">
                    <div className="flex justify-between items-center">
                      <div>
                        <span className="font-medium text-slate-700">Total Price:</span>
                        <p className="text-xs text-slate-500">Pay via Fygaro after submission</p>
                      </div>
                      <span className="text-3xl font-bold text-cyan-600">${selectedPrice}</span>
                    </div>
                  </div>
                </div>

                {/* Submit */}
                <div className="flex justify-between gap-3 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                    Cancel
                  </Button>
                  <div className="flex gap-2">
                    <Button 
                      type="submit" 
                      className="bg-cyan-500 hover:bg-cyan-600"
                      disabled={submitting}
                      data-testid="submit-ad"
                    >
                      {submitting ? 'Submitting...' : 'Submit Ad for Review'}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => {
                        if (AD_PRICING[adForm.duration]?.fygaro_link) {
                          window.open(AD_PRICING[adForm.duration].fygaro_link, '_blank');
                        }
                      }}
                      className="bg-green-500 hover:bg-green-600"
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      Pay ${selectedPrice}
                    </Button>
                  </div>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* My Ads List */}
          <Card>
            <CardHeader>
              <CardTitle>My Advertisements</CardTitle>
              <CardDescription>Track and manage your ad campaigns</CardDescription>
            </CardHeader>
            <CardContent>
              {myAds.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Megaphone className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                  <p className="text-lg mb-2">No advertisements yet</p>
                  <p className="text-sm mb-4">Select a package above to create your first ad!</p>
                  <Button onClick={() => handlePricingClick('1_week')} className="bg-cyan-500 hover:bg-cyan-600">
                    Create Your First Ad
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {myAds.map((ad) => {
                    const daysRemaining = getDaysRemaining(ad.end_date);
                    return (
                      <div 
                        key={ad.id} 
                        className={`border rounded-lg p-4 ${
                          ad.status === 'active' && daysRemaining !== null && daysRemaining <= 3 
                            ? 'border-orange-300 bg-orange-50' 
                            : ''
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          {(ad.image_url || ad.company_logo_url) && (
                            <img 
                              src={toFullUrl(ad.image_url || ad.company_logo_url)} 
                              alt={ad.title}
                              className="w-28 h-20 object-cover rounded"
                            />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className="font-semibold">{ad.title}</h3>
                              {getStatusBadge(ad.status)}
                              {ad.duration && (
                                <Badge variant="outline">{AD_PRICING[ad.duration]?.label || ad.duration}</Badge>
                              )}
                            </div>
                            {ad.description && (
                              <p className="text-sm text-slate-600 line-clamp-2">{ad.description}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2 text-xs text-slate-400 flex-wrap">
                              {ad.status === 'active' && (
                                <>
                                  <span className="flex items-center gap-1">
                                    <Eye className="h-3 w-3" />
                                    {ad.impressions || 0} views
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <BarChart3 className="h-3 w-3" />
                                    {ad.clicks || 0} clicks
                                  </span>
                                  {daysRemaining !== null && (
                                    <span className={`flex items-center gap-1 ${daysRemaining <= 3 ? 'text-orange-600 font-medium' : ''}`}>
                                      <Clock className="h-3 w-3" />
                                      {daysRemaining} days remaining
                                    </span>
                                  )}
                                </>
                              )}
                              {ad.price > 0 && (
                                <span className="flex items-center gap-1">
                                  <DollarSign className="h-3 w-3" />
                                  ${ad.price} {ad.paid ? '(Paid)' : '(Pending Payment)'}
                                </span>
                              )}
                            </div>
                            
                            {/* Expiring Soon Warning */}
                            {ad.status === 'active' && daysRemaining !== null && daysRemaining <= 3 && (
                              <div className="mt-3 flex items-center justify-between bg-orange-100 p-3 rounded">
                                <div className="flex items-center gap-2 text-orange-700">
                                  <AlertCircle className="h-4 w-4" />
                                  <span className="text-sm font-medium">Your ad expires soon!</span>
                                </div>
                                <Button
                                  size="sm"
                                  onClick={() => handlePayNow(ad)}
                                  className="bg-orange-500 hover:bg-orange-600"
                                >
                                  Renew Now
                                </Button>
                              </div>
                            )}
                            
                            {/* Payment Button for Pending */}
                            {(ad.status === 'pending' || ad.status === 'approved') && !ad.paid && (
                              <div className="mt-3">
                                <Button
                                  size="sm"
                                  onClick={() => handlePayNow(ad)}
                                  className="bg-green-500 hover:bg-green-600"
                                >
                                  <CreditCard className="h-4 w-4 mr-2" />
                                  Complete Payment - ${AD_PRICING[ad.duration]?.price || ad.price}
                                </Button>
                              </div>
                            )}
                            
                            {ad.status === 'pending' && (
                              <div className="mt-3 flex items-center gap-2 text-sm text-yellow-600 bg-yellow-50 p-2 rounded">
                                <Clock className="h-4 w-4" />
                                <span>Under review. Pay now to speed up approval.</span>
                              </div>
                            )}
                            {ad.status === 'rejected' && ad.admin_notes && (
                              <div className="mt-3 flex items-start gap-2 text-sm text-red-600 bg-red-50 p-2 rounded">
                                <AlertCircle className="h-4 w-4 mt-0.5" />
                                <span>Reason: {ad.admin_notes}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Help Section */}
          <Card className="mt-6 border-cyan-200 bg-cyan-50/50">
            <CardContent className="p-4">
              <h4 className="font-semibold mb-2">Need Help?</h4>
              <p className="text-sm text-slate-600">
                For custom campaigns, bulk discounts, or questions about advertising, 
                contact us at <a href="mailto:ads@ctccargonetwork.com" className="text-cyan-600 hover:underline">ads@ctccargonetwork.com</a>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default Advertising;
